import { GoTriangleDown, GoTriangleUp } from 'react-icons/go';

import { rankItem } from '@tanstack/match-sorter-utils';
import { LinkText, Status } from 'components/Table/Table.styles';
import { I18nType, OrderRowData } from 'types';

import { FilterFn, SortDirection } from '@tanstack/react-table';
import Link from 'next/link';
import { JSX } from 'react';

const isEmpty = (element: string) => !element || element.length === 0;

const formatDate = (element: string | string[]) => {
  let stringToDate: string | JSX.Element[];

  try {
    if (typeof element === 'string') {
      stringToDate = new Date(element.split('T')[0]).toLocaleDateString(
        'en-us'
      );
      return stringToDate;
    }

    stringToDate = element.map((date: string) => (
      <div key={date}>{new Date(date).toLocaleDateString('en-us')}</div>
    ));
    return stringToDate;
  } catch (error) {
    return 'Invalid Date';
  }
};

export const isHeaderNeedSorting = (id: string) => {
  return id !== 'tracking' && id !== 'displayTotal';
};

const formatTrackingID = (url: string) => {
  const urlSearchParams = new URLSearchParams(url);

  const params = Object.fromEntries(urlSearchParams.entries());
  const trackingID = params.InquiryNumber1;

  return trackingID;
};

export const renderOrderNumber = (orderNumber: string) => {
  if (isEmpty(orderNumber)) {
    return '-';
  }

  return (
    <Link href={`orderdetails/${orderNumber}`} passHref legacyBehavior>
      <LinkText>{orderNumber}</LinkText>
    </Link>
  );
};

export const renderSubscriptionOrderNumbers = (
  orderNumbers: string[],
  i18n: I18nType
) => {
  return orderNumbers?.length ? i18n['Subscription'] : i18n['One Time'];
};

export const renderIteroOrderNumber = (
  proposalName: string,
  proposalSalesforceId: string
) => {
  return (
    <Link
      href={`/iteroscanners/orderdetails/${proposalSalesforceId}`}
      passHref
      legacyBehavior
    >
      <LinkText>{proposalName}</LinkText>
    </Link>
  );
};

export const renderDate = (cellData: any) => {
  if (isEmpty(cellData)) {
    return '-';
  } else {
    return <>{formatDate(cellData)}</>;
  }
};

export const renderTrackingID = (
  trackingUrls: string[],
  trackingNumbers: string | []
) => {
  return trackingUrls?.length === 0
    ? '-'
    : trackingUrls.map((link, index) => (
        <a key={link} href={link} target="_blank" rel="noreferrer">
          <LinkText>{trackingNumbers[index]}</LinkText>
          <br />
        </a>
      ));
};

export const renderIteroTrackingID = (trackingNumber: string) => {
  return (
    <a
      key={`https://www.ups.com/track?tracknum=${trackingNumber}`}
      href={`https://www.ups.com/track?tracknum=${trackingNumber}`}
      target="_blank"
      rel="noreferrer"
    >
      <LinkText>{trackingNumber}</LinkText>
      <br />
    </a>
  );
};

export const renderStatus = (orderStatus: any, i18n: I18nType) => (
  <Status orderStatus={orderStatus?.toLocaleLowerCase()}>
    <div>
      {orderStatus?.length === 0 ||
      orderStatus === null ||
      orderStatus === 'system approved'
        ? ''
        : i18n[orderStatus] ?? orderStatus}
    </div>
  </Status>
);

// INSPIRATION: https://bobbyhadz.com/blog/javascript-count-duplicates-in-array
/* INFO: solution from above URL slightly modified, and instead of returning an object,
 returned an array, and the statuses stored as array properties
 https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Indexed_collections#using_arrays_to_store_other_properties */
const calculateTabMenuStatuses = (
  tabData: { status: string; [key: string]: any }[]
) => {
  // Extract the 'status' field from each element in 'tabData', or an empty array if 'tabData' is undefined
  const statusesOnlyArray = tabData?.map(object => object.status) ?? [];

  // Initialize an object to count the number of occurrences of each status
  const countedStatuses: { [status: string]: number } = {};
  // Iterate over the array of statuses
  statusesOnlyArray.forEach(status => {
    // If the status is defined (not falsy), increment the count for that status in the countedStatuses object
    if (status) {
      countedStatuses[status] = (countedStatuses[status] || 0) + 1;
    }
  });

  // Convert the countedStatuses object to an array of objects, with each object having a 'status' and 'orderCount' field
  // Return the array of statuses
  return Object.entries(countedStatuses).map(([status, orderCount]) => ({
    status,
    orderCount,
  }));
};

export const renderHeader = (
  headerText: string,
  sortingInfo: false | SortDirection
) => {
  const ascendSorted = sortingInfo === 'asc';
  const descendSorted = sortingInfo === 'desc';

  return (
    <div style={{ display: 'flex', alignContent: 'end' }}>
      <div>{headerText}</div>
      <div style={{ display: 'block', marginLeft: 5 }}>
        <div style={{ height: 7 }}>
          <GoTriangleUp
            style={{ color: ascendSorted ? '' : 'rgba(0, 0, 0, 0.44)' }}
          />
        </div>
        <div style={{ height: 7 }}>
          <GoTriangleDown
            style={{ color: descendSorted ? '' : 'rgba(0, 0, 0, 0.44)' }}
          />
        </div>
      </div>
    </div>
  );
};

export const tabHeaders = (
  tabData: { status: any; [key: string]: any }[],
  i18n: I18nType
) => {
  const allOrderStatus = i18n['All Order'] ?? 'All Order';
  return [{ status: allOrderStatus }, ...calculateTabMenuStatuses(tabData)];
};

export const setTableContent = (
  value: string,
  setValue: React.Dispatch<React.SetStateAction<string | number>>,
  i18n: I18nType
) =>
  value.toLowerCase() === 'all order'
    ? setValue(_prevValue => '')
    : setValue(_prevValue => value);

export const setTableSize = (table: any, size: number) => {
  table.setPageSize(size);
};
export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

export const handleStatusColor = (orderStatus: string) => {
  switch (orderStatus) {
    case 'processing':
    case 'pending':
    case 'partial':
    case 'in review':
      return '#0277b5';
    case 'delivered':
    case 'ready':
    case 'shipped':
    case 'approved':
    case 'active':
      return '#257F49';
    case 'delayed':
    case 'backorder':
    case 'draft':
    case 'suspended':
      return '#CE6215';
    case 'cancelled':
    case 'rejected':
      return '#CF2E38';
    default:
      return '';
  }
};

export const handleStatusBackground = (orderStatus: string) => {
  switch (orderStatus) {
    case 'processing':
    case 'pending':
    case 'partial':
    case 'in review':
      return '#d4f0ff';
    case 'delivered':
    case 'ready':
    case 'shipped':
    case 'approved':
    case 'active':
      return '#CBF5D8';
    case 'delayed':
    case 'backorder':
    case 'draft':
    case 'suspended':
      return '#FFE7D1';
    case 'cancelled':
    case 'rejected':
      return '#FFE6E8';
    default:
      return '';
  }
};
